import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatMetric, isBlockHidden } from './panelConfig';
import SortHeader from './components/SortHeader';
import CatalogHealth from './CatalogHealth';
import PriceSection from './components/PriceSection';
import './CatalogSection.css';

const EMPTY = [];

const EVO_SERIES = [
  { key: 'continuing', label: 'Continúan', color: '#94a3b8' },
  { key: 'restocked', label: 'Restockeados', color: '#f59e0b' },
  { key: 'new', label: 'Nuevos', color: '#10b981' },
];

function EvoTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-medium text-gray-900">{label}</div>
      {payload
        .slice()
        .reverse()
        .map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-gray-600">
              <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
              {p.name}
            </span>
            <span className="tabular-nums font-medium text-gray-900">{p.value}</span>
          </div>
        ))}
      <div className="mt-1 flex justify-between gap-4 border-t border-gray-100 pt-1 text-gray-500">
        <span>En stock</span>
        <span className="tabular-nums font-semibold">{total}</span>
      </div>
    </div>
  );
}

/**
 * Barras apiladas de SKUs en stock por día: continúan + restockeados + nuevos.
 * "Restockeado" = ID conocido que no estaba en stock el día anterior; "nuevo" =
 * ID nunca visto antes.
 */
function SkuEvolutionChart({ data }) {
  const rows = useMemo(
    () => (data ?? []).map((d) => ({ ...d, label: d.date.slice(5).replace('-', '/') })),
    [data]
  );
  if (rows.length === 0) return null;

  return (
    <div className="cat-panel">
      <div className="cat-h2">Evolución de SKUs en stock</div>
      <p className="cat-sub">
        SKUs disponibles por día: los que <strong>continúan</strong> en stock, los{' '}
        <strong>restockeados</strong> (un ID que había quedado sin stock y volvió) y los{' '}
        <strong>nuevos</strong> (un ID que no teníamos antes).
      </p>
      <div className="h-64 w-full" style={{ marginTop: 14 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} width={40} allowDecimals={false} />
            <Tooltip content={<EvoTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              iconType="square"
              iconSize={10}
              formatter={(v) => <span className="text-gray-600">{v}</span>}
            />
            {EVO_SERIES.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stackId="s"
                fill={s.color}
                radius={i === EVO_SERIES.length - 1 ? [2, 2, 0, 0] : 0}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const STATUS = {
  agotado: { label: 'Agotado', cls: 'bg-red-50 text-red-700' },
  discontinuado: { label: 'Discontinuado', cls: 'bg-amber-50 text-amber-700' },
};

const COLUMNS = [
  { key: 'units_sold', label: 'Vendidas', format: 'number' },
  { key: 'revenue', label: 'Revenue', format: 'currency' },
];

function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    }
    return dir === 'asc' ? av - bv : bv - av;
  });
}

function InfoTooltip({ text }) {
  return (
    <span className="group relative inline-flex">
      <Info className="h-3.5 w-3.5 shrink-0 cursor-help text-gray-400" aria-label={text} />
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 w-56 -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function Card({ label, value, hint, danger, tooltip }) {
  return (
    <div className={`cat-stat ${danger ? 'danger' : ''}`}>
      <div className="k" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="v">{value}</div>
      {hint && <div className="h">{hint}</div>}
    </div>
  );
}

function Cell({ col, row }) {
  const v = row[col.key];
  return <td>{v == null ? '—' : formatMetric(v, col.format)}</td>;
}

/**
 * Sección "Catálogo": SKUs que vendieron en el período pero ya no se pueden
 * vender — agotados (stock 0 en el catálogo de hoy) o discontinuados (no
 * aparecen en el catálogo de hoy). El revenue que se está dejando pasar.
 */
export default function CatalogSection({ brandId, data, isLoading, isError, health, healthLoading, healthError, priceExplorerFetcher }) {
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);
  const items = data?.items ?? EMPTY;
  const summary = data?.summary ?? {};
  const rows = useMemo(() => sortRows(items, sort.key, sort.dir), [items, sort]);
  const visibleRows = showAll ? rows : rows.slice(0, 10);
  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-500">Cargando catálogo…</p>;
  if (isError) return <p className="py-8 text-center text-sm text-red-600">No se pudo cargar el catálogo.</p>;

  return (
    <div className="cat">
      {/* 1. Evolución de stock */}
      <SkuEvolutionChart data={data?.sku_evolution} />

      {/* 2. SKUs sin stock con venta */}
      <div className="cat-panel">
        <div className="cat-h2">SKUs sin stock con venta</div>
        <p className="cat-sub">
          Productos agotados o discontinuados que vendieron en el período — revenue que estás dejando pasar.
        </p>

        <div className="cat-strip" style={{ marginTop: 14 }}>
          <Card
            label="SKUs sin stock con venta"
            value={formatMetric(summary.sku_count ?? 0, 'number')}
            hint={`${summary.agotados ?? 0} agotados · ${summary.discontinuados ?? 0} discontinuados`}
            tooltip="Cantidad de productos que vendieron en el período seleccionado pero hoy están agotados (stock 0) o discontinuados (ya no figuran en el catálogo)."
          />
          <Card
            label="Revenue en riesgo"
            value={formatMetric(summary.revenue_at_risk ?? 0, 'currency')}
            danger
            tooltip="Facturación de esos productos en el período seleccionado. Es la venta que podrías estar perdiendo por no tener stock."
          />
          <Card
            label="% Revenue en riesgo"
            value={formatMetric(summary.revenue_at_risk_pct, 'percent')}
            danger
            tooltip="Del total facturado en el período, qué porción proviene de productos que ya no tenés en stock (revenue en riesgo ÷ revenue total del período)."
          />
          <Card
            label="Unidades vendidas"
            value={formatMetric(summary.units_at_risk ?? 0, 'number')}
            tooltip="Unidades vendidas de esos productos durante el período seleccionado."
          />
        </div>

        {rows.length === 0 ? (
          <div className="cat-empty sm">Sin quiebres de stock en el rango — los productos vendidos tienen stock.</div>
        ) : (
          <div className="cat-tablewrap" style={{ marginTop: 16 }}>
            <table className="cat-table">
              <thead>
                <tr>
                  <SortHeader sortKey="product_name" sort={sort} onSort={onSort} align="left">
                    Producto
                  </SortHeader>
                  {COLUMNS.map((c) => (
                    <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort}>
                      {c.label}
                    </SortHeader>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((it) => {
                  const st = STATUS[it.status] ?? STATUS.agotado;
                  return (
                    <tr key={it.sku}>
                      <td>
                        {it.product_name || it.sku}
                        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${st.cls}`} style={{ marginLeft: 8 }}>{st.label}</span>
                        <span className="sku">{it.sku}</span>
                      </td>
                      {COLUMNS.map((c) => (
                        <Cell key={c.key} col={c} row={it} />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > 10 && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="mt-3 text-sm font-medium text-[#1B3DFB] hover:underline"
              >
                {showAll ? 'Ver menos' : `Ver todos (${rows.length})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 3. Explorador de precios */}
      {priceExplorerFetcher && <PriceSection fetcher={priceExplorerFetcher} currency={data?.currency} />}

      {/* 4. Stock inmovilizado */}
      {!isBlockHidden(brandId, 'catalog_stock_inmovilizado') && (
        <CatalogHealth health={health} isLoading={healthLoading} isError={healthError} />
      )}
    </div>
  );
}
